<template>
  <div class="TripDisplayConnector">
    <div
      class="TripDisplayConnector-tripType"
      :class="{ 'TripDisplayConnector-tripType--is-connecting': !isDirect }"
    >
      <p v-if="isDirect">
        {{ t('ui.booking.availability.results.direct') }}
      </p>

      <template v-else>
        <p>
          {{ t('ui.booking.availability.results.stopsCount', nbOfConnections) }}
        </p>

        <p class="TripDisplayConnector-stopCodes">
          {{ stopsDetailLabel }}
        </p>
      </template>
    </div>

    <div class="TripDisplayConnector-connector">
      <template v-if="isDirect">
        <div class="TripDisplayConnector-connectorLine" />

        <div class="TripDisplayConnector-iconContainer">
          <Icon :icon="icon" :size="iconSize" class="TripDisplayConnector-icon" />
        </div>

        <div class="TripDisplayConnector-connectorLine" />
      </template>

      <template v-else>
        <div class="TripDisplayConnector-connectorLine" />

        <template v-for="(_, index) of nbOfConnections" :key="index">
          <div class="TripDisplayConnector-iconContainer TripDisplayConnector-iconContainer--is-connecting">
            <Icon icon="circle" size="xs" class="TripDisplayConnector-icon" />
          </div>

          <div class="TripDisplayConnector-connectorLine" />
        </template>
      </template>
    </div>

    <p class="TripDisplayConnector-duration">
      {{ formattedDuration }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useDurationFormatter, useResponsive } from '@cvo/ui/composables';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '../../../atoms/Icon/Icon.vue';
import type { TripDisplayConnectorProps } from './TripDisplayConnector.model';

const props = withDefaults(defineProps<TripDisplayConnectorProps>(), {
  icon: 'airplane',
});

const { t } = useI18n();
const { isMobile } = useResponsive();
const { formatDuration } = useDurationFormatter();

const iconSize = computed(() => (isMobile.value ? 'xs' : 'md'));

const formattedDuration = computed(() => formatDuration(props.duration));

const nbOfConnections = computed(() => {
  if (props.stops !== undefined || props.connections !== undefined) {
    return (props.stops ?? 0) + (props.connections ?? 0);
  }
  return Math.max(0, props.segments.length - 1);
});

const isDirect = computed(() => nbOfConnections.value === 0);

const stopsDetailLabel = computed(() => {
  return props.segments
    .slice(1)
    .map((connection, index) => {
      const previousConnection = props.segments[index];

      return t('ui.booking.availability.results.stops', {
        iata: previousConnection.arrival.station.code,
        stopDuration: formatDuration(connection.stopDuration),
      });
    })
    .join(', ');
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/mixins';

@layer cvo-ui {
  .TripDisplayConnector {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    min-width: 38px; // avoid having almost 0px connector lines

    &-tripType {
      color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-secondary-color;
      @include text.text(xs);
      text-align: center;

      &--is-connecting {
        color: vars.$organisms-tripdisplay-segmentdetails-multi-stop-default-text-highlight-color;
        font-weight: vars.$typography-weight-strong;
      }
    }

    &-connector {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      position: relative;

      &::before,
      &::after {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: vars.$organisms-tripdisplay-segmentdetails-direct-default-connector-border-color;
        flex-shrink: 0;
        position: absolute;

        @include responsive.mobile {
          width: 4px;
          height: 4px;
        }
      }

      &::before {
        inset-inline-start: 0;
      }

      &::after {
        inset-inline-end: 0;
      }

      .TripDisplayConnector-connectorLine {
        flex: 1;
        height: vars.$organisms-tripdisplay-segmentdetails-direct-default-connector-border-stroke;
        background-color: vars.$organisms-tripdisplay-segmentdetails-direct-default-connector-border-color;
        min-width: 0;
      }

      .TripDisplayConnector-iconContainer {
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(45deg);
        margin: 0 vars.$dimensions-d-1;
        color: vars.$organisms-tripdisplay-segmentdetails-direct-default-icon-connector-color;

        &--is-connecting {
          color: vars.$organisms-tripdisplay-segmentdetails-one-stop-default-icon-connector-color;
          margin: 0;

          + .TripDisplayConnector-connectorLine:not(:last-child) {
            flex: 0 0 vars.$dimensions-d-4;
          }
        }

        @include mixins.rtl() {
          transform: rotate(-135deg);
        }
      }
    }

    &-duration {
      color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-secondary-color;
      @include text.text(xs);
      text-align: center;
    }
  }
}
</style>
